/**
 * NW Mix & Matcha bundle builder (mix grid + sibling sticky).
 * Sticky outside [data-nw-bundle-main] via data-nw-sticky-bundle-selector.
 * Grid products share one paid-line flow: no main-set / free-pouch / bundle quantity caps; cart lines have no parent_id.
 * Promo config: window.nwGlobalPromoConfig (nw-global-promo-config.liquid).
 * Sticky UI config: window.nwMixMatchaStickyBarConfig (nw-mix-matcha-sticky-inner.liquid).
 * Cart lines mirror: window.nwMixMatchaBundleSelectedLines.
 */
(() => {
  'use strict';

  /**
   * Cart-ready list of sticky-bar lines (Mix & Matcha page only), refreshed after every add / remove / qty / promo sync.
   * Each: { lineKey, kind, variantId, productId, quantity, sellingPlanId, parentId } — parentId is always null on this flow.
   */
  window.nwMixMatchaBundleSelectedLines = window.nwMixMatchaBundleSelectedLines || [];

  const SEL = {
    bundleRoot: '[data-nw-bundle-main]',
    stickyBar: '[data-sticky-bar]',
    ctaButton: '.nw-bundle-item__product-item-cta-button',
    qtyWrap: '.nw-bundle-item__product-item-cta-quantity',
    qtyInput: '.nw-bundle-item__product-item-cta-quantity-input',
    stickySlot: '.nw-bundle-sticky-bar__item',
    stickyProduct: '.nw-bundle-sticky-bar__item-product',
    stickyTitle: '.nw-bundle-sticky-bar__item-product-title',
    stickyPrice: '.nw-bundle-sticky-bar__item-product-price',
    stickyImg: '.nw-bundle-sticky-bar__item-product-image img',
    removeSticky: '[data-remove-item]',
    decQty: '[data-decrease-quantity]',
    incQty: '[data-increase-quantity]',
    bundleColumn: '.nw-bundle-item',
  };

  const LINE_KIND = {
    PAID: 'paid',
    FREE_POUCH: 'freePouch',
    PROMO_GIFT: 'promoGift',
  };

  /** Line item property on every Mix & Matcha bundle line (`/cart/add.js`). */
  const NW_MIX_MATCHA_BUNDLE_PROP = '_mix_matcha_bundle';

  /** Shown subscription total = one-time bundle subtotal × (1 - this). */
  const NW_PURCHASE_SUBSCRIPTION_DISCOUNT = 0.1;

  /** Pixels to leave below the viewport top when auto-scrolling the next bundle column (sticky header clearance). */
  const NW_BUNDLE_STEP_SCROLL_OFFSET_PX = 96;

  function getCartAddJsUrl() {
    if (typeof KROWN !== 'undefined' && KROWN.settings && KROWN.settings.routes && KROWN.settings.routes.cart_add_url) {
      return `${KROWN.settings.routes.cart_add_url}.js`;
    }
    return '/cart/add.js';
  }

  /** `nw-bundle-purchase` radios: one-time vs subscription (defaults to onetime if block missing). */
  function nwBundlePurchaseModeFromBundleRoot(bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    const purchase = root?.querySelector('.nw-bundle-purchase');
    const radio = purchase?.querySelector('input[name="nw-bundle-purchase"]:checked');
    return radio && radio.value === 'subscription' ? 'subscription' : 'onetime';
  }

  /** Merge bundle lines for Cart API: `onetime` omits selling_plan; `subscription` adds it when present on the line. Mix & Matcha: all lines are top-level (no parent_id). */
  function buildCartItemsFromBundleLines(lines, mode, bundleRoot) {
    /** @type {Map<string, { id: number, quantity: number, selling_plan?: number }>} */
    const merge = new Map();
    for (const line of lines) {
      if (line == null || line.kind === LINE_KIND.PROMO_GIFT) continue;
      if (line.variantId == null || line.variantId === '') continue;
      const vid = parseInt10(line.variantId, 0);
      if (!vid) continue;
      const qty = Math.max(1, parseInt10(line.quantity, 1));
      let sellingPlan;
      if (mode === 'subscription' && line.sellingPlanId != null && String(line.sellingPlanId).trim() !== '') {
        const pid = parseInt10(line.sellingPlanId, 0);
        if (pid) sellingPlan = pid;
      }

      const key = `${vid}__${sellingPlan || 0}`;
      const cur = merge.get(key);
      if (cur) {
        cur.quantity += qty;
      } else {
        merge.set(key, { id: vid, quantity: qty, selling_plan: sellingPlan });
      }
    }
    return [...merge.values()].map((item) => {
      /** @type {{ id: number, quantity: number, selling_plan?: number, properties: Record<string, string> }} */
      const payload = {
        id: item.id,
        quantity: item.quantity,
        properties: { [NW_MIX_MATCHA_BUNDLE_PROP]: 'true' },
      };
      if (item.selling_plan) payload.selling_plan = item.selling_plan;
      return payload;
    });
  }

  function nwBundleRefreshCartFromSection() {
    if (typeof fetch === 'undefined') return Promise.resolve();
    return fetch('?section_id=helper-cart')
      .then((r) => r.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const cartForm = document.getElementById('AjaxCartForm');
        const cartSub = document.getElementById('AjaxCartSubtotal');
        const formSrc = doc.getElementById('AjaxCartForm');
        const subSrc = doc.getElementById('AjaxCartSubtotal');
        if (cartForm && formSrc) {
          cartForm.innerHTML = formSrc.innerHTML;
          if (typeof cartForm.ajaxifyCartItems === 'function') cartForm.ajaxifyCartItems();
        }
        if (cartSub && subSrc) cartSub.innerHTML = subSrc.innerHTML;
        const countSrc = doc.querySelector('#AjaxCartForm [data-cart-count]');
        if (countSrc) {
          document.querySelectorAll('[data-header-cart-count]').forEach((elm) => {
            elm.textContent = countSrc.textContent;
          });
        }
        const totalSrc = doc.querySelector('#AjaxCartForm [data-cart-total]');
        if (totalSrc) {
          document.querySelectorAll('[data-header-cart-total]').forEach((elm) => {
            elm.textContent = totalSrc.textContent;
          });
        }
      });
  }

  /** Refresh theme cart fragments and notify Upcart / cart drawer listeners. */
  function nwBundleNotifyCartAfterAdd() {
    return nwBundleRefreshCartFromSection().then(() => {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new Event('add-to-cart', { bubbles: true }));
    });
  }

  /**
   * @param {'onetime' | 'subscription'} mode
   */
  function nwBundleAddToCart(mode, bundleRoot, triggerEl) {
    const lines = window.nwMixMatchaBundleSelectedLines || [];
    const items = buildCartItemsFromBundleLines(lines, mode, bundleRoot);
    if (items.length === 0) return;

    if (triggerEl) triggerEl.classList.add('working');

    const fail = (msg) => {
      window.alert(msg);
      if (triggerEl) triggerEl.classList.remove('working');
    };

    fetch(getCartAddJsUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json().then((data) => ({ response, data })))
      .then(({ response, data }) => {
        if (response.status === 422 || data.status === 422) {
          const msg =
            typeof data.description === 'string'
              ? data.description
              : typeof data.message === 'string'
                ? data.message
                : 'Could not add to cart';
          fail(msg);
          return;
        }
        if (!response.ok) {
          fail('Could not add to cart');
          return;
        }
        return nwBundleNotifyCartAfterAdd();
      })
      .catch((err) => {
        if (err instanceof Error && err.message && !err.message.includes('fetch')) {
          fail(err.message);
        } else {
          fail('Could not add to cart');
        }
      })
      .finally(() => {
        if (triggerEl) triggerEl.classList.remove('working');
      });
  }

  /** Added while free pouch shows selected label (for CSS hooks). */
  const CLS_FREE_POUCH_SELECTED = 'nw-bundle-item__product-item-cta-button--is-selected';
  /** Sticky cell for the main-set SKU when that column is configured as a free gift (see nw-bundle-item). */
  const CLS_STICKY_MAIN_SET_FREE_GIFT = 'nw-bundle-sticky-bar__item--main-set-free-gift';

  const ATTR_PAID_ACTIVE = 'data-nw-paid-active';

  /** @returns {NwStickyConfig} */
  function getStickyConfig() {
    const global = window.nwGlobalPromoConfig || {};
    const local = window.nwMixMatchaStickyBarConfig || {};
    return {
      ...global,
      ...local,
      shippingThreshold: global.shippingThreshold ?? 3499,
      tieredDiscounts: global.tieredDiscounts || [],
      freeGifts: global.freeGifts || [],
      freeGiftThresholdMajorUnits: false,
    };
  }

  function formatMoney(cents) {
    const n = Number(cents);
    if (Number.isNaN(n)) return '—';
    if (typeof window.KROWN !== 'undefined' && KROWN.settings && KROWN.settings.shop_money_format) {
      return formatShopifyCents(n, KROWN.settings.shop_money_format);
    }
    return (n / 100).toFixed(2);
  }

  function formatShopifyCents(cents, formatString) {
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    if (!match) return (cents / 100).toFixed(2);

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      const iso = (typeof KROWN !== 'undefined' && KROWN.settings && KROWN.settings.iso_code) ? KROWN.settings.iso_code : '';
      return dollarsAmount + centsAmount + iso;
    }

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
      default:
        value = (cents / 100).toFixed(2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  function parseInt10(v, fallback = 0) {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? fallback : n;
  }

  function getStickyRoot() {
    return document.querySelector(SEL.stickyBar);
  }

  function getBundleRoot() {
    return document.querySelector(SEL.bundleRoot);
  }

  /** Sticky outside the bundle wrapper (sibling section) can point here via data-nw-sticky-bundle-selector. */
  function resolveBundleRootForSticky(stickyEl) {
    if (!stickyEl) return null;
    const nested = stickyEl.closest(SEL.bundleRoot);
    if (nested) return nested;
    const sel = (stickyEl.getAttribute('data-nw-sticky-bundle-selector') || '').trim();
    if (sel) {
      try {
        const el = document.querySelector(sel);
        if (el && el.matches(SEL.bundleRoot)) return el;
      } catch (e) {
        /* invalid selector */
      }
    }
    return null;
  }

  function getStickyForBundleRoot(bundleRoot) {
    if (!bundleRoot) return null;
    if (bundleRoot.__nwStickyRoot instanceof HTMLElement) return bundleRoot.__nwStickyRoot;
    return bundleRoot.querySelector(SEL.stickyBar);
  }

  function getStickyItemTemplate(stickyRoot) {
    const bar = stickyRoot?.closest?.(SEL.stickyBar) || stickyRoot;
    if (!bar) return null;
    const tid = (bar.getAttribute('data-sticky-item-template-id') || '').trim();
    if (tid) {
      const byId = document.getElementById(tid);
      if (byId && byId.content) return byId;
    }
    return document.getElementById('nw-bundle-sticky-bar__item-template');
  }

  function isEmptyStaticPlaceholder(el) {
    return (
      el != null &&
      el.matches(SEL.stickySlot) &&
      el.classList.contains('nw-bundle-sticky-bar__item--empty') &&
      !el.hasAttribute('data-nw-dynamic-promo') &&
      !el.hasAttribute('data-nw-dynamic-user')
    );
  }

  /** Sticky indices reserved for bundle messaging (free shipping / gift); never receive main, pouch, or paid extras. */
  function isBundleMilestoneOnlySlot(slot) {
    if (!slot) return false;
    return (
      slot.hasAttribute('data-nw-bundle-shipping-milestone') ||
      slot.hasAttribute('data-nw-bundle-gift-milestone') ||
      slot.hasAttribute('data-nw-bundle-gift-milestone-2')
    );
  }

  /**
   * Reserved static column for threshold gift 1 or 2 (when configured in theme).
   * @param {1|2|string|number} cfgSlot — freeGifts[].slot from config
   */
  function findStaticPromoGiftMilestoneSlot(stickyRoot, cfgSlot) {
    if (!stickyRoot) return null;
    const n = Number(cfgSlot);
    const sel =
      n === 2 ? '[data-nw-bundle-gift-milestone-2]' : '[data-nw-bundle-gift-milestone]';
    const el = stickyRoot.querySelector(sel);
    if (!el || !el.matches(SEL.stickySlot)) return null;
    if (!el.classList.contains('nw-bundle-sticky-bar__item--empty')) return null;
    if (el.hasAttribute('data-nw-dynamic-promo') || el.hasAttribute('data-nw-dynamic-user')) return null;
    return el;
  }

  /** Paid / pouch columns in DOM order (excludes shipping + gift milestones and promo template rows). */
  function getProductEligibleSlotsInOrder(stickyRoot) {
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    if (!items) return [];
    const out = [];
    for (const s of items.querySelectorAll(SEL.stickySlot)) {
      if (s.hasAttribute('data-nw-dynamic-promo')) continue;
      if (isBundleMilestoneOnlySlot(s)) continue;
      out.push(s);
    }
    return out;
  }

  /** An empty product slot appears before a filled paid/pouch line (middle remove leaves a hole). */
  function movableSlotsHaveLeadingGap(stickyRoot) {
    const eligible = getProductEligibleSlotsInOrder(stickyRoot);
    let seenEmpty = false;
    for (const s of eligible) {
      const empty = s.classList.contains('nw-bundle-sticky-bar__item--empty');
      const movableFilled =
        !empty &&
        (s.dataset.nwLineKind === LINE_KIND.PAID || s.dataset.nwLineKind === LINE_KIND.FREE_POUCH);
      if (empty) seenEmpty = true;
      else if (seenEmpty && movableFilled) return true;
    }
    return false;
  }

  function stickySlotToPayload(slot) {
    const kind = slot.dataset.nwLineKind;
    const prod = slot.querySelector(SEL.stickyProduct);
    const imgEl = prod?.querySelector(SEL.stickyImg);
    const image = (imgEl && imgEl.getAttribute('src')) || '';
    const title = prod?.querySelector(SEL.stickyTitle)?.textContent?.trim() || '';
    const unitCents = parseInt10(slot.dataset.nwUnitCents, 0);
    const qty = parseInt10(slot.dataset.nwQty, 1);
    const sp = slot.dataset.nwSellingPlanId;
    const pid = slot.dataset.nwProductId;
    const payload = {
      lineKey: slot.dataset.nwLineKey,
      kind,
      variantId: slot.dataset.nwVariantId,
      productId: pid != null && pid !== '' ? String(pid) : null,
      sellingPlanId: sp != null && String(sp).trim() !== '' ? String(sp) : null,
      title,
      image,
      unitCents,
      qty,
    };
    if (kind === LINE_KIND.FREE_POUCH) {
      payload.priceLabel = 'Free';
    }
    return payload;
  }

  function removeTrailingEmptyDynamicUserSlots(stickyRoot) {
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    if (!items) return;
    const slots = [...items.querySelectorAll(SEL.stickySlot)];
    let lastNonEmpty = -1;
    slots.forEach((s, i) => {
      if (!s.classList.contains('nw-bundle-sticky-bar__item--empty')) lastNonEmpty = i;
    });
    for (let i = slots.length - 1; i > lastNonEmpty; i--) {
      const s = slots[i];
      if (
        s.hasAttribute('data-nw-dynamic-user') &&
        s.classList.contains('nw-bundle-sticky-bar__item--empty')
      ) {
        s.remove();
      }
    }
  }

  /** Shift paid + free-pouch lines left into the first eligible cells (milestones stay fixed). */
  function recompactMovableStickySlots(stickyRoot) {
    if (!stickyRoot) return;
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    if (!items) return;

    const movable = [...items.querySelectorAll(SEL.stickySlot)].filter(
      (s) =>
        !s.classList.contains('nw-bundle-sticky-bar__item--empty') &&
        (s.dataset.nwLineKind === LINE_KIND.PAID || s.dataset.nwLineKind === LINE_KIND.FREE_POUCH)
    );

    if (movable.length === 0) {
      removeTrailingEmptyDynamicUserSlots(stickyRoot);
      return;
    }

    const payloads = movable.map(stickySlotToPayload);
    for (const s of movable) {
      clearStickySlot(s);
    }

    let targets = getProductEligibleSlotsInOrder(stickyRoot);
    let anchor = targets.length ? targets[targets.length - 1] : null;
    while (targets.length < payloads.length) {
      const n = insertDynamicUserSlotAfter(stickyRoot, anchor);
      if (!n) break;
      targets.push(n);
      anchor = n;
    }

    const nFill = Math.min(payloads.length, targets.length);
    for (let i = 0; i < nFill; i++) {
      fillStickySlot(targets[i], payloads[i], { scrollIntoView: false });
    }
    if (nFill > 0) {
      ensureStickySlotVisibleInItemsRow(targets[nFill - 1]);
    }

    removeTrailingEmptyDynamicUserSlots(stickyRoot);
  }

  /**
   * Next placeholder for user-selected lines only (excludes dynamic promo cells — they never consume tier slots).
   */
  function findFirstEmptySlot(stickyRoot) {
    if (!stickyRoot) return null;
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    const scope = items || stickyRoot;
    for (const slot of scope.querySelectorAll(`${SEL.stickySlot}.nw-bundle-sticky-bar__item--empty`)) {
      if (slot.hasAttribute('data-nw-dynamic-promo')) continue;
      if (slot.hasAttribute('data-nw-dynamic-user')) continue;
      if (isBundleMilestoneOnlySlot(slot)) continue;
      return slot;
    }
    return null;
  }

  /** First empty tier placeholder strictly to the right of `afterEl` (same strip). */
  function findNextEmptyStaticAfter(afterEl) {
    let n = afterEl ? afterEl.nextElementSibling : null;
    while (n && n.matches(SEL.stickySlot)) {
      if (isEmptyStaticPlaceholder(n) && !isBundleMilestoneOnlySlot(n)) return n;
      n = n.nextElementSibling;
    }
    return null;
  }

  /** Last non-empty slot in the strip (DOM order), so promos sit after the final user-selected cell. */
  function findLastFilledItemSlot(stickyRoot) {
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    if (!items) return null;
    const slots = [...items.querySelectorAll(SEL.stickySlot)].filter(
      (s) => !s.classList.contains('nw-bundle-sticky-bar__item--empty')
    );
    return slots.length ? slots[slots.length - 1] : null;
  }

  /**
   * `.nw-bundle-sticky-bar__items` uses overflow-x: auto; nudge scroll when a filled slot is outside the visible strip.
   */
  function ensureStickySlotVisibleInItemsRow(slot) {
    if (!slot || typeof slot.getBoundingClientRect !== 'function') return;
    const items = slot.closest('.nw-bundle-sticky-bar__items');
    if (!items) return;
    const pad = 16;
    const prefersReducedMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = prefersReducedMotion ? 'auto' : 'smooth';

    const run = () => {
      const wrap = items.getBoundingClientRect();
      const r = slot.getBoundingClientRect();
      if (r.width <= 0 && r.height <= 0) return;
      let dx = 0;
      if (r.left < wrap.left + pad) {
        dx = r.left - wrap.left - pad;
      } else if (r.right > wrap.right - pad) {
        dx = r.right - wrap.right + pad;
      }
      if (dx !== 0) {
        items.scrollBy({ left: dx, behavior });
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(run);
    });
  }

  /**
   * Clone tag-free slot markup; insert directly after `insertAfterEl`, or append if no anchor.
   */
  function insertDynamicPromoSlotAfter(stickyRoot, insertAfterEl) {
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    if (!items) return null;
    const template = getStickyItemTemplate(stickyRoot);
    if (!template || !template.content || !template.content.firstElementChild) return null;
    const node = /** @type {HTMLElement} */ (template.content.firstElementChild.cloneNode(true));
    if (insertAfterEl && items.contains(insertAfterEl)) {
      insertAfterEl.insertAdjacentElement('afterend', node);
    } else {
      items.appendChild(node);
    }
    return node;
  }

  /**
   * Extra static placeholder cells for paid qty (same structure as promo template, tagged as user-dynamic).
   */
  function insertDynamicUserSlotAfter(stickyRoot, insertAfterEl) {
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    if (!items) return null;
    const template = getStickyItemTemplate(stickyRoot);
    if (!template || !template.content || !template.content.firstElementChild) return null;
    const node = /** @type {HTMLElement} */ (template.content.firstElementChild.cloneNode(true));
    node.removeAttribute('data-nw-dynamic-promo');
    node.setAttribute('data-nw-dynamic-user', '');
    if (insertAfterEl && items.contains(insertAfterEl)) {
      insertAfterEl.insertAdjacentElement('afterend', node);
    } else {
      items.appendChild(node);
    }
    return node;
  }

  function makePaidLineKey(variantId) {
    const suffix =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
    return `paid-${variantId}::${suffix}`;
  }

  function paidSlotsForVariant(stickyRoot, variantId) {
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    if (!items) return [];
    const vid = String(variantId);
    const out = [];
    for (const s of items.querySelectorAll(`${SEL.stickySlot}:not(.nw-bundle-sticky-bar__item--empty)`)) {
      if (s.dataset.nwLineKind === LINE_KIND.PAID && String(s.dataset.nwVariantId) === vid) {
        out.push(s);
      }
    }
    return out;
  }

  function removePaidSlotElement(slot) {
    if (!slot) return;
    if (slot.hasAttribute('data-nw-dynamic-user')) {
      slot.remove();
    } else {
      clearStickySlot(slot);
    }
  }

  function addPaidUnitSlot(stickyRoot, btn, bundleRoot) {
    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId || !stickyRoot) return false;
    const lineKey = makePaidLineKey(variantId);
    const unit = unitCentsFromButton(btn);

    const lastGlobal = findLastFilledItemSlot(stickyRoot);
    let slot = lastGlobal ? findNextEmptyStaticAfter(lastGlobal) : null;
    if (!slot) {
      slot = findFirstEmptySlot(stickyRoot);
    }
    if (!slot) {
      slot = insertDynamicUserSlotAfter(stickyRoot, lastGlobal);
    }
    if (!slot) return false;
    fillStickySlot(slot, {
      lineKey,
      kind: LINE_KIND.PAID,
      variantId,
      productId: btn.getAttribute('data-product-id'),
      sellingPlanId: btn.getAttribute('data-selling-plan-id'),
      title: btn.getAttribute('data-product-title') || '',
      image: btn.getAttribute('data-product-image') || '',
      unitCents: unit,
      qty: 1,
    });
    return true;
  }

  /** Grow or shrink paid sticky cells to match `targetQ` (each cell qty 1). */
  function syncPaidSlotsToQty(stickyRoot, bundleRoot, btn, targetQ) {
    const vid = btn.getAttribute('data-variant-id');
    if (!vid || !stickyRoot) return 0;
    const root = bundleRoot || getBundleRoot();
    let n = paidSlotsForVariant(stickyRoot, vid).length;
    if (targetQ > n) {
      while (n < targetQ) {
        if (!addPaidUnitSlot(stickyRoot, btn, root)) break;
        n = paidSlotsForVariant(stickyRoot, vid).length;
      }
    } else if (targetQ < n) {
      while (n > targetQ) {
        const slots = paidSlotsForVariant(stickyRoot, vid);
        const last = slots[slots.length - 1];
        if (!last) break;
        removePaidSlotElement(last);
        n--;
      }
    }
    return paidSlotsForVariant(stickyRoot, vid).length;
  }

  function clearStickySlot(slot) {
    if (!slot) return;
    slot.classList.add('nw-bundle-sticky-bar__item--empty');
    slot.classList.remove(CLS_STICKY_MAIN_SET_FREE_GIFT);
    delete slot.dataset.nwLineKey;
    delete slot.dataset.nwLineKind;
    delete slot.dataset.nwVariantId;
    delete slot.dataset.nwQty;
    delete slot.dataset.nwUnitCents;
    delete slot.dataset.nwPromoSlot;
    delete slot.dataset.nwProductId;
    delete slot.dataset.nwSellingPlanId;
    const prod = slot.querySelector(SEL.stickyProduct);
    if (!prod) return;
    const titleEl = prod.querySelector(SEL.stickyTitle);
    const priceEl = prod.querySelector(SEL.stickyPrice);
    const img = prod.querySelector(SEL.stickyImg);
    if (titleEl) titleEl.textContent = '';
    if (priceEl) priceEl.textContent = '';
    if (img) {
      img.src = '';
      img.alt = '';
    }
    const rm = prod.querySelector(SEL.removeSticky);
    if (rm) rm.hidden = false;
  }

  /**
   * @param {HTMLElement} slot
   * @param {{
   *   lineKey: string,
   *   kind: string,
   *   variantId: string,
   *   title: string,
   *   image: string,
   *   unitCents: number,
   *   qty: number,
   *   priceLabel?: string,
   *   promoSlot?: string,
   *   productId?: string | null,
   *   sellingPlanId?: string | null
   * }} payload
   * @param {{ scrollIntoView?: boolean }} [options] — default true; set false for batched fills (e.g. recompact).
   */
  function fillStickySlot(slot, payload, options) {
    const scrollIntoView = options == null || options.scrollIntoView !== false;
    const prod = slot.querySelector(SEL.stickyProduct);
    if (!prod) return;
    slot.classList.remove('nw-bundle-sticky-bar__item--empty');
    slot.dataset.nwLineKey = payload.lineKey;
    slot.dataset.nwLineKind = payload.kind;
    slot.dataset.nwVariantId = String(payload.variantId);
    slot.dataset.nwQty = String(payload.qty);
    slot.dataset.nwUnitCents = String(payload.unitCents);
    if (payload.promoSlot != null) slot.dataset.nwPromoSlot = String(payload.promoSlot);
    if (payload.productId != null && payload.productId !== '') {
      slot.dataset.nwProductId = String(payload.productId);
    } else {
      delete slot.dataset.nwProductId;
    }
    if (payload.sellingPlanId != null && payload.sellingPlanId !== '') {
      slot.dataset.nwSellingPlanId = String(payload.sellingPlanId);
    } else {
      delete slot.dataset.nwSellingPlanId;
    }

    const titleEl = prod.querySelector(SEL.stickyTitle);
    const priceEl = prod.querySelector(SEL.stickyPrice);
    const img = prod.querySelector(SEL.stickyImg);
    if (titleEl) titleEl.textContent = payload.title;
    const priceText =
      payload.priceLabel != null
        ? payload.priceLabel
        : formatMoney(payload.unitCents * payload.qty);
    if (priceEl) priceEl.textContent = priceText;
    if (img) {
      let url = payload.image || '';
      if (payload.kind === LINE_KIND.PROMO_GIFT) {
        const tagActive = img.getAttribute('data-nw-bundle-tag-active-src');
        if (tagActive) url = tagActive;
      }
      img.src = url;
      img.alt = payload.title;
    }
    const rm = prod.querySelector(SEL.removeSticky);
    if (rm) rm.hidden = payload.kind === LINE_KIND.PROMO_GIFT;

    if (payload.kind === LINE_KIND.PROMO_GIFT) {
      const gifts = Array.isArray(getStickyConfig().freeGifts) ? getStickyConfig().freeGifts : [];
      const gMatch = gifts.find((x) => String(x.slot) === String(payload.promoSlot));
      syncPromoGiftBundleTag(slot, gMatch || { slot: payload.promoSlot });
    }
    syncMainSetFreeGiftSlotClass(slot);
    if (scrollIntoView) {
      ensureStickySlotVisibleInItemsRow(slot);
    }
  }

  function lineKeyForFreePouch(btn) {
    return `free-pouch-${btn.getAttribute('data-variant-id')}`;
  }

  function lineKeyForPromoGift(cfgSlot) {
    return `promo-gift-${cfgSlot}`;
  }

  function isFreePouchButton(btn) {
    return btn.hasAttribute('data-is-free-gift');
  }

  function isMainSetPaidButton(btn) {
    return btn != null && btn.hasAttribute('data-is-main-set');
  }

  /** Paid add-on SKUs (not main set, not free pouch). Gated until main set + free pouch are chosen. */
  function isExtrasPaidButton(btn) {
    return btn != null && !isFreePouchButton(btn) && !isMainSetPaidButton(btn);
  }

  function unitCentsFromButton(btn) {
    if (isFreePouchButton(btn)) return 0;
    return parseInt10(btn.getAttribute('data-product-price-cents'), 0);
  }

  function escapeSelectorAttr(value) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  /** True when this slot is the shopper’s main-set choice and the main-set column is a free-gift block. */
  function isStickySlotMainSetFreeGift(slot, bundleRoot) {
    if (!slot || !bundleRoot) return false;
    const vid = slot.dataset.nwVariantId;
    if (!vid) return false;
    const btn = bundleRoot.querySelector(
      `${SEL.ctaButton}[data-is-main-set][data-is-free-gift][data-variant-id="${escapeSelectorAttr(String(vid))}"]`
    );
    if (!btn) return false;
    const kind = slot.dataset.nwLineKind;
    const key = slot.dataset.nwLineKey;
    if (kind === LINE_KIND.FREE_POUCH) {
      return btn.hasAttribute('data-nw-line-key') && btn.getAttribute('data-nw-line-key') === key;
    }
    if (kind === LINE_KIND.PAID) {
      if (!btn.hasAttribute(ATTR_PAID_ACTIVE)) return false;
      const stickyRoot = slot.closest(SEL.stickyBar);
      return stickyRoot ? paidSlotsForVariant(stickyRoot, vid).includes(slot) : false;
    }
    return false;
  }

  function syncMainSetFreeGiftSlotClass(slot) {
    if (!slot) return;
    const root = slot.closest(SEL.bundleRoot);
    slot.classList.toggle(
      CLS_STICKY_MAIN_SET_FREE_GIFT,
      root ? isStickySlotMainSetFreeGift(slot, root) : false
    );
  }

  function paidCtaForVariant(root, variantId) {
    if (!root || variantId == null || String(variantId).trim() === '') return null;
    return root.querySelector(
      `${SEL.ctaButton}[data-variant-id="${escapeSelectorAttr(String(variantId))}"]:not([data-is-free-gift])`
    );
  }

  function slotByLineKey(stickyRoot, lineKey) {
    return stickyRoot.querySelector(`${SEL.stickySlot}[data-nw-line-key="${escapeSelectorAttr(lineKey)}"]`);
  }

  function resetPaidButtonUI(btn) {
    const wrap = btn.closest('.nw-bundle-item__product-item-cta');
    if (!wrap) return;
    const qtyEl = wrap.querySelector(SEL.qtyWrap);
    btn.classList.remove('hidden');
    btn.removeAttribute('data-nw-line-key');
    btn.removeAttribute(ATTR_PAID_ACTIVE);
    if (qtyEl) {
      qtyEl.classList.add('hidden');
      const input = qtyEl.querySelector(SEL.qtyInput);
      if (input) input.value = '1';
    }
  }

  function deselectPaidLine(btn, stickyRoot, bundleRoot) {
    if (!btn.hasAttribute(ATTR_PAID_ACTIVE)) return;
    const sticky = stickyRoot || getStickyRoot();
    const vid = btn.getAttribute('data-variant-id');
    if (sticky && vid) {
      for (const s of [...paidSlotsForVariant(sticky, vid)]) {
        removePaidSlotElement(s);
      }
    }
    resetPaidButtonUI(btn);
    if (sticky) {
      reconcileAfterStickyChange(sticky, bundleRoot);
    }
  }

  function activatePaidButtonUI(btn) {
    const wrap = btn.closest('.nw-bundle-item__product-item-cta');
    if (!wrap) return;
    const qtyEl = wrap.querySelector(SEL.qtyWrap);
    btn.classList.add('hidden');
    if (qtyEl) qtyEl.classList.remove('hidden');
  }

  function getFilledSlots(stickyRoot) {
    return [...stickyRoot.querySelectorAll(`${SEL.stickySlot}:not(.nw-bundle-sticky-bar__item--empty)`)];
  }

  /**
   * Mix grid: paid CTAs without main-set / free-pouch roles; no global quantity caps (per-line max comes from input).
   */
  function syncMixGridPaidCtaStates(bundleRoot, stickyRoot) {
    const root = bundleRoot || getBundleRoot();
    const sticky = stickyRoot || getStickyRoot();
    if (!root || !sticky) return;
    for (const btn of root.querySelectorAll(`${SEL.ctaButton}:not([data-is-free-gift]):not([data-is-main-set])`)) {
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      const wrap = btn.closest('.nw-bundle-item__product-item-cta');
      const inc = wrap?.querySelector(SEL.incQty);
      if (!inc) continue;
      const selected = btn.hasAttribute(ATTR_PAID_ACTIVE);
      if (!selected) {
        inc.disabled = false;
        continue;
      }
      const vid = btn.getAttribute('data-variant-id');
      const thisN = vid ? paidSlotsForVariant(sticky, vid).length : 0;
      const input = wrap.querySelector(SEL.qtyInput);
      const maxAttr = parseInt10(input?.getAttribute('max'), 100);
      inc.disabled = thisN >= maxAttr;
    }
  }

  function reconcileAfterStickyChange(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (movableSlotsHaveLeadingGap(stickyRoot)) {
      recompactMovableStickySlots(stickyRoot);
    }
    syncPromoFreeGifts(stickyRoot);
    updateSummary(stickyRoot, root);
  }

  /**
   * Subtotal in cents for shipping & tier eligibility: paid lines only (exclude free pouch + promo gift).
   * Free-gift thresholds use {@link giftQualificationSubtotalCents} (tier-discounted paid subtotal).
   */
  function subtotalAmountCents(stickyRoot) {
    let sum = 0;
    for (const slot of getFilledSlots(stickyRoot)) {
      if (slot.dataset.nwLineKind !== LINE_KIND.PAID) continue;
      const unit = parseInt10(slot.dataset.nwUnitCents, 0);
      const qty = parseInt10(slot.dataset.nwQty, 1);
      sum += unit * qty;
    }
    return sum;
  }

  /** Subtotal for free-gift thresholds: paid lines with active tier % off (matches summary total). */
  function giftQualificationSubtotalCents(stickyRoot) {
    const cfg = getStickyConfig();
    const paidSubtotal = subtotalAmountCents(stickyRoot);
    return tierDiscountedAmountCents(paidSubtotal, cfg.tieredDiscounts || [], paidSubtotal);
  }

  /** @param {number} subtotalCents Paid bundle subtotal (minor units), before discount codes. */
  function tierMinAmountCents(tier) {
    if (tier == null) return 0;
    if (tier.minAmount != null && tier.minAmount !== '') return Number(tier.minAmount);
    if (tier.minItems != null && tier.minItems !== '') return Number(tier.minItems);
    return 0;
  }

  function pickActiveTieredDiscount(tieredDiscounts, subtotalCents) {
    if (!Array.isArray(tieredDiscounts) || tieredDiscounts.length === 0) return null;
    const sorted = [...tieredDiscounts].sort((a, b) => tierMinAmountCents(b) - tierMinAmountCents(a));
    for (const tier of sorted) {
      if (subtotalCents >= tierMinAmountCents(tier)) return tier;
    }
    return null;
  }

  /** Apply active tier %; tier eligibility uses pre-discount {@link tierBasisCents} (defaults to amountCents). */
  function tierDiscountedAmountCents(amountCents, tieredDiscounts, tierBasisCents) {
    const basis = tierBasisCents != null ? tierBasisCents : amountCents;
    const tier = pickActiveTieredDiscount(tieredDiscounts, basis);
    const pct = tier != null ? Number(tier.valuePercent) : NaN;
    if (tier && Number.isFinite(pct) && pct > 0) {
      return Math.round(amountCents * (1 - pct / 100));
    }
    return amountCents;
  }

  /** Product grid: tier badge, discounted price, and strikethrough pre-tier total (× line qty when selected). */
  function getMixGridCardQty(btn, stickyRoot) {
    if (!btn?.hasAttribute(ATTR_PAID_ACTIVE)) return 1;
    const wrap = btn.closest('.nw-bundle-item__product-item-cta');
    const input = wrap?.querySelector(SEL.qtyInput);
    if (input) return Math.max(1, parseInt10(input.value, 1));
    const vid = btn.getAttribute('data-variant-id');
    if (!stickyRoot || !vid) return 1;
    return Math.max(1, paidSlotsForVariant(stickyRoot, vid).length);
  }

  function restoreMixGridCardPrice(card, btn) {
    const unitCents = parseInt10(btn.getAttribute('data-product-price-cents'), 0);
    const priceCurrent =
      card.querySelector('[data-nw-mix-grid-price-current]') ||
      card.querySelector('.nw-mix-matcha-bundle__price-current');
    const tierStrike = card.querySelector('[data-nw-mix-grid-tier-strike]');
    const compareAt = card.querySelector('[data-nw-mix-grid-compare-at]');
    if (priceCurrent && unitCents > 0) priceCurrent.textContent = formatMoney(unitCents);
    if (tierStrike) {
      tierStrike.textContent = '';
      tierStrike.hidden = true;
      tierStrike.classList.add('hidden');
      tierStrike.setAttribute('aria-hidden', 'true');
    }
    if (compareAt) {
      const compareCents = parseInt10(compareAt.getAttribute('data-nw-compare-at-cents'), 0);
      const showCompare = compareCents > unitCents;
      compareAt.hidden = !showCompare;
      compareAt.classList.toggle('hidden', !showCompare);
      compareAt.toggleAttribute('aria-hidden', !showCompare);
      if (showCompare) compareAt.textContent = formatMoney(compareCents);
    }
  }

  function syncMixMatchaGridTierDiscountLabels(bundleRoot, stickyRoot, activeTier) {
    const root = bundleRoot || getBundleRoot();
    const sticky = stickyRoot || getStickyRoot();
    if (!root) return;
    const pct = activeTier != null ? Number(activeTier.valuePercent) : NaN;
    const show = Boolean(activeTier && Number.isFinite(pct) && pct > 0);
    for (const card of root.querySelectorAll('.nw-mix-matcha-bundle__card')) {
      const btn = card.querySelector(`${SEL.ctaButton}:not([data-is-free-gift]):not([data-is-main-set])`);
      if (!btn) continue;
      const badge = card.querySelector('[data-nw-mix-grid-tier-discount]');
      const priceCurrent =
        card.querySelector('[data-nw-mix-grid-price-current]') ||
        card.querySelector('.nw-mix-matcha-bundle__price-current');
      const tierStrike = card.querySelector('[data-nw-mix-grid-tier-strike]');
      const compareAt = card.querySelector('[data-nw-mix-grid-compare-at]');
      const unitCents = parseInt10(btn.getAttribute('data-product-price-cents'), 0);

      if (badge) {
        if (show) {
          badge.textContent = `-${Math.round(pct)}%`;
          badge.removeAttribute('hidden');
          badge.classList.remove('hidden');
        } else {
          badge.textContent = '';
          badge.setAttribute('hidden', '');
          badge.classList.add('hidden');
        }
      }

      if (!priceCurrent || unitCents <= 0) continue;

      if (show) {
        const qty = getMixGridCardQty(btn, sticky);
        const originalTotal = unitCents * qty;
        const discountedTotal = Math.round(originalTotal * (1 - pct / 100));
        priceCurrent.textContent = formatMoney(discountedTotal);
        if (tierStrike) {
          tierStrike.textContent = formatMoney(originalTotal);
          tierStrike.hidden = false;
          tierStrike.classList.remove('hidden');
          tierStrike.removeAttribute('aria-hidden');
        }
        if (compareAt) {
          compareAt.hidden = true;
          compareAt.classList.add('hidden');
          compareAt.setAttribute('aria-hidden', 'true');
        }
      } else {
        restoreMixGridCardPrice(card, btn);
      }
    }
  }

  function hasUserSelectedBundleItems(stickyRoot) {
    return getFilledSlots(stickyRoot).some(
      (s) =>
        s.dataset.nwLineKind === LINE_KIND.PAID || s.dataset.nwLineKind === LINE_KIND.FREE_POUCH
    );
  }

  function updateStickyBarVisibility(stickyRoot) {
    if (!stickyRoot) return;
    stickyRoot.classList.toggle('visible', hasUserSelectedBundleItems(stickyRoot));
  }

  function syncNwBundleSelectedLines(stickyRoot) {
    if (!stickyRoot) {
      window.nwMixMatchaBundleSelectedLines = [];
      return;
    }
    const lines = [];
    for (const slot of getFilledSlots(stickyRoot)) {
      const vid = slot.dataset.nwVariantId || null;
      lines.push({
        lineKey: slot.dataset.nwLineKey || null,
        kind: slot.dataset.nwLineKind || null,
        variantId: vid,
        productId: slot.dataset.nwProductId || null,
        quantity: parseInt10(slot.dataset.nwQty, 1),
        sellingPlanId: slot.dataset.nwSellingPlanId || null,
        parentId: null,
      });
    }
    window.nwMixMatchaBundleSelectedLines = lines;
  }

  /** One summary row per threshold promo gift (DOM strip order); placeholder row when none. */
  function renderSummaryPromoGifts(stickyRoot) {
    const mount = stickyRoot?.querySelector('[data-nw-summary-gifts-mount]');
    if (!mount) return;
    mount.replaceChildren();
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    const ordered = items
      ? [...items.querySelectorAll(SEL.stickySlot)].filter(
          (s) =>
            s.dataset.nwLineKind === LINE_KIND.PROMO_GIFT &&
            !s.classList.contains('nw-bundle-sticky-bar__item--empty')
        )
      : [];

    function appendRow(titleText, valueText) {
      const row = document.createElement('div');
      row.className = 'nw-bundle-sticky-bar__summary-item';
      const titleEl = document.createElement('div');
      titleEl.className = 'nw-bundle-sticky-bar__summary-item-title';
      titleEl.textContent = titleText;
      const valEl = document.createElement('div');
      valEl.className = 'nw-bundle-sticky-bar__summary-item-value';
      valEl.textContent = valueText;
      row.append(titleEl, valEl);
      mount.appendChild(row);
    }

    if (ordered.length === 0) {
      appendRow('Gift', '—');
      return;
    }

    ordered.forEach((slot, i) => {
      const t = slot.querySelector(SEL.stickyTitle);
      const name = (t && t.textContent.trim()) || 'Free gift';
      const label = ordered.length > 1 ? `Gift ${i + 1}` : 'Gift';
      appendRow(label, name);
    });
  }

  /** Paid product cells in strip DOM order (left → right), bundle lines only. */
  function getPaidSlotsInDomOrder(stickyRoot) {
    const items = stickyRoot.querySelector('.nw-bundle-sticky-bar__items');
    if (!items) return [];
    const out = [];
    for (const s of items.querySelectorAll(SEL.stickySlot)) {
      if (s.classList.contains('nw-bundle-sticky-bar__item--empty')) continue;
      if (s.dataset.nwLineKind === LINE_KIND.PAID) out.push(s);
    }
    return out;
  }

  /**
   * First paid line (running sum) that crosses tierMinCents: cumulative before line &lt; threshold &lt;= after.
   */
  function findMarginalUnlockSlotIndex(paidSlots, tierMinCents) {
    if (!paidSlots.length || tierMinCents <= 0) return -1;
    let before = 0;
    for (let i = 0; i < paidSlots.length; i++) {
      const unit = parseInt10(paidSlots[i].dataset.nwUnitCents, 0);
      const qty = parseInt10(paidSlots[i].dataset.nwQty, 1);
      const line = unit * qty;
      const after = before + line;
      if (before < tierMinCents && after >= tierMinCents) return i;
      before = after;
    }
    return -1;
  }

  function tierUnlockBadgeStyleRef(stickyRoot) {
    return stickyRoot?.querySelector('[data-nw-tier-discount-milestone]');
  }

  /** Empty free-shipping milestone slot: switch placeholder → active art when paid subtotal meets shipping threshold. */
  function syncShippingMilestoneSlotVisuals(stickyRoot, subtotalCents) {
    if (!stickyRoot) return;
    const cfg = getStickyConfig();
    const shipTh = parseInt10(cfg.shippingThreshold, 0);
    const qualified = shipTh > 0 && subtotalCents >= shipTh;
    for (const slot of stickyRoot.querySelectorAll('[data-nw-bundle-shipping-milestone]')) {
      if (!slot.classList.contains('nw-bundle-sticky-bar__item--empty')) {
        slot.classList.remove('nw-bundle-sticky-bar__item--shipping-unlocked');
        continue;
      }
      const placeholder = slot.querySelector('.nw-bundle-sticky-bar__item-placeholder');
      const prodImg = slot.querySelector(SEL.stickyImg);
      const actSrc = (prodImg && prodImg.getAttribute('data-nw-bundle-tag-active-src')) || '';
      const phImg = slot.querySelector('.nw-bundle-sticky-bar__item-placeholder-img');
      const phSrc =
        (phImg && phImg.getAttribute('data-nw-shipping-placeholder-src')) || (phImg && phImg.src) || '';
      let fallback = placeholder?.querySelector('[data-nw-shipping-milestone-fallback-img]') || null;
      if (qualified && actSrc) {
        if (phImg) {
          phImg.src = actSrc;
          phImg.hidden = false;
        } else if (placeholder) {
          if (!fallback) {
            fallback = document.createElement('img');
            fallback.className = 'nw-bundle-sticky-bar__item-placeholder-img';
            fallback.setAttribute('data-nw-shipping-milestone-fallback-img', '');
            fallback.alt = '';
            fallback.width = 40;
            fallback.height = 40;
            placeholder.appendChild(fallback);
          }
          fallback.src = actSrc;
          fallback.hidden = false;
          placeholder.querySelector('svg')?.setAttribute('hidden', '');
        }
        slot.classList.add('nw-bundle-sticky-bar__item--shipping-unlocked');
      } else {
        if (phImg && phSrc) phImg.src = phSrc;
        if (phImg) phImg.hidden = false;
        if (fallback) {
          fallback.remove();
        }
        placeholder?.querySelector('svg')?.removeAttribute('hidden');
        slot.classList.remove('nw-bundle-sticky-bar__item--shipping-unlocked');
      }
    }
  }

  /**
   * Tier strip UX: (1) Liquid column tags on *paid* cells are hidden; (2) each tier badge is placed on
   * the first paid line (DOM order) whose running subtotal crosses that tier’s min — the “unlock” line;
   * (3) empty milestone columns hide when superseded or when that tier is attributed to a product.
   */
  function syncTierDiscountMilestoneVisuals(stickyRoot, subtotalCents) {
    if (!stickyRoot) return;
    const cfg = getStickyConfig();
    const tiers = Array.isArray(cfg.tieredDiscounts) ? cfg.tieredDiscounts : [];
    const useAttribution = tiers.length > 0;
    const activeTier = pickActiveTieredDiscount(tiers, subtotalCents);
    const highestTierNum =
      activeTier != null && activeTier.tier != null && activeTier.tier !== ''
        ? Number(activeTier.tier)
        : 0;

    for (const el of stickyRoot.querySelectorAll('[data-nw-active-tier-summary]')) {
      el.remove();
    }
    for (const el of stickyRoot.querySelectorAll('[data-nw-tier-unlock-attributed]')) {
      el.remove();
    }

    const attributedTierNums = new Set();
    const styleRef = tierUnlockBadgeStyleRef(stickyRoot);

    if (useAttribution) {
      const paidSlots = getPaidSlotsInDomOrder(stickyRoot);
      const tiersAsc = [...tiers].sort((a, b) => tierMinAmountCents(a) - tierMinAmountCents(b));
      /** One badge per product cell: if one add crosses several thresholds, show only the highest %. */
      const slotToBestTier = new Map();
      for (const tier of tiersAsc) {
        const minC = tierMinAmountCents(tier);
        if (minC <= 0) continue;
        if (subtotalCents < minC) continue;
        const tNum =
          tier.tier != null && tier.tier !== '' ? Number(tier.tier) : NaN;
        if (!Number.isFinite(tNum) || tNum <= 0) continue;
        const pct = Number(tier.valuePercent);
        if (!Number.isFinite(pct) || pct <= 0) continue;
        const idx = findMarginalUnlockSlotIndex(paidSlots, minC);
        if (idx < 0) continue;
        const slot = paidSlots[idx];
        const prev = slotToBestTier.get(slot);
        if (!prev || pct > Number(prev.valuePercent)) {
          slotToBestTier.set(slot, tier);
        }
      }
      for (const [slot, tier] of slotToBestTier) {
        const tNum =
          tier.tier != null && tier.tier !== '' ? Number(tier.tier) : NaN;
        if (!Number.isFinite(tNum) || tNum <= 0) continue;
        const pct = Number(tier.valuePercent);
        if (!Number.isFinite(pct) || pct <= 0) continue;
        let wrap = slot.querySelector('.nw-bundle-sticky-bar__item-discount-tags');
        if (!wrap) {
          wrap = document.createElement('div');
          wrap.className = 'nw-bundle-sticky-bar__item-discount-tags';
          slot.appendChild(wrap);
        }
        const span = document.createElement('span');
        span.setAttribute('data-nw-tier-unlock-attributed', String(tNum));
        span.className = 'nw-bundle-sticky-bar__item-discount-tag';
        span.textContent = `Save ${pct}%`;
        if (styleRef && styleRef.style) {
          span.style.setProperty(
            '--discount-tag-bg',
            styleRef.style.getPropertyValue('--discount-tag-bg') || '#FFC107'
          );
          span.style.setProperty(
            '--discount-tag-color',
            styleRef.style.getPropertyValue('--discount-tag-color') || '#201D1E'
          );
        }
        wrap.appendChild(span);
        attributedTierNums.add(tNum);
      }
    }

    for (const slot of stickyRoot.querySelectorAll(SEL.stickySlot)) {
      const tierTag = slot.querySelector('[data-nw-tier-number]');
      if (!tierTag) {
        slot.classList.remove('nw-bundle-sticky-bar__item--tier-column-suppressed');
        continue;
      }

      const tierNum = parseInt10(tierTag.getAttribute('data-nw-tier-number'), 0);
      const isEmpty = slot.classList.contains('nw-bundle-sticky-bar__item--empty');
      const min = parseInt10(tierTag.getAttribute('data-nw-tier-min-cents'), 0);
      const hasMilestone = tierTag.hasAttribute('data-nw-tier-discount-milestone');
      const qualified = hasMilestone && min > 0 && subtotalCents >= min;

      if (hasMilestone) {
        tierTag.classList.toggle('nw-bundle-sticky-bar__item-discount-tag--threshold-pending', !qualified);
      }

      const isPaid = !isEmpty && slot.dataset.nwLineKind === LINE_KIND.PAID;
      if (useAttribution && isPaid && hasMilestone) {
        tierTag.classList.add('nw-bundle-sticky-bar__item-discount-tag--hidden-for-active-tier');
      } else {
        tierTag.classList.remove('nw-bundle-sticky-bar__item-discount-tag--hidden-for-active-tier');
      }

      const suppressEmpty =
        isEmpty &&
        highestTierNum > 0 &&
        tierNum > 0 &&
        (tierNum < highestTierNum || attributedTierNums.has(tierNum));
      if (suppressEmpty) {
        slot.classList.add('nw-bundle-sticky-bar__item--tier-column-suppressed');
      } else {
        slot.classList.remove('nw-bundle-sticky-bar__item--tier-column-suppressed');
      }

      if (
        isEmpty &&
        !slot.classList.contains('nw-bundle-sticky-bar__item--tier-column-suppressed') &&
        hasMilestone
      ) {
        slot.classList.toggle('nw-bundle-sticky-bar__item--tier-milestone-unlocked', qualified);
      } else {
        slot.classList.remove('nw-bundle-sticky-bar__item--tier-milestone-unlocked');
      }
    }
  }

  /** Compact summary (v2): savings line, "{pct}% Discount", strikethrough pre-tier total + discounted total. */
  function updateSummaryV2(stickyRoot, subtotal, cfg) {
    const wrap = stickyRoot?.querySelector('[data-nw-summary-v2-wrap]');
    if (!wrap) return;
    const tierBlock = wrap.querySelector('[data-nw-summary-v2-tier-block]');
    const saveEl = wrap.querySelector('[data-nw-summary-v2-save]');
    const discEl = wrap.querySelector('[data-nw-summary-v2-discount]');
    const origEl = wrap.querySelector('[data-nw-summary-v2-price-original]');
    const finalEl = wrap.querySelector('[data-nw-summary-v2-price-final]');
    const savePrefix = (wrap.dataset.savePrefix || 'You save').trim();
    const discountSuffix = (wrap.dataset.discountSuffix || 'Discount').trim();

    const tier = pickActiveTieredDiscount(cfg.tieredDiscounts || [], subtotal);
    const pct = tier != null ? Number(tier.valuePercent) : NaN;
    const showTierPromo = Boolean(tier && Number.isFinite(pct) && pct > 0);

    if (tierBlock) tierBlock.hidden = !showTierPromo;

    if (showTierPromo) {
      const discounted = Math.round(subtotal * (1 - pct / 100));
      const saveCents = Math.max(0, subtotal - discounted);
      if (saveEl) saveEl.textContent = `${savePrefix} ${formatMoney(saveCents)}`.trim();
      if (discEl) discEl.textContent = `${pct}% ${discountSuffix}`.trim();
      if (origEl) {
        origEl.textContent = formatMoney(subtotal);
        origEl.hidden = false;
      }
      if (finalEl) finalEl.textContent = formatMoney(bundleCheckoutOneTimeDisplayCents(stickyRoot, subtotal));
    } else {
      if (origEl) {
        origEl.textContent = '';
        origEl.hidden = true;
      }
      if (finalEl) finalEl.textContent = formatMoney(bundleCheckoutOneTimeDisplayCents(stickyRoot, subtotal));
    }
  }

  function updateSummary(stickyRoot, bundleRoot) {
    const cfg = getStickyConfig();
    const itemsLabel = stickyRoot?.querySelector('[data-nw-summary-items-label]');
    const subEl = stickyRoot?.querySelector('[data-nw-summary-subtotal]');
    const shipEl = stickyRoot?.querySelector('[data-nw-summary-shipping]');
    const tierRow = stickyRoot?.querySelector('[data-nw-summary-tier-row]');
    const tierEl = stickyRoot?.querySelector('[data-nw-summary-tier]');

    const subtotal = subtotalAmountCents(stickyRoot);
    updateSummaryV2(stickyRoot, subtotal, cfg);
    const totalPieces = getFilledSlots(stickyRoot).reduce((acc, s) => acc + parseInt10(s.dataset.nwQty, 1), 0);

    if (itemsLabel) {
      const prefix = totalPieces === 1 ? '1 item ' : `${totalPieces} items `;
      const prefixEl = itemsLabel.querySelector('.nw-bundle-sticky-bar__summary-items-label-prefix');
      const selectedEl = itemsLabel.querySelector('.nw-bundle-sticky-bar__summary-items-label-selected');
      if (prefixEl && selectedEl) {
        prefixEl.textContent = prefix;
        selectedEl.textContent = 'selected';
      } else {
        itemsLabel.textContent =
          totalPieces === 1 ? '1 item selected' : `${totalPieces} items selected`;
      }
    }
    if (subEl) {
      const tierForDisplay = pickActiveTieredDiscount(cfg.tieredDiscounts, subtotal);
      const pctD = tierForDisplay != null ? Number(tierForDisplay.valuePercent) : NaN;
      if (tierForDisplay && Number.isFinite(pctD) && pctD > 0) {
        const discounted = Math.round(subtotal * (1 - pctD / 100));
        subEl.innerHTML = `<span class="nw-bundle-sticky-bar__subtotal-original">${formatMoney(subtotal)}</span> <span class="nw-bundle-sticky-bar__subtotal-discounted">${formatMoney(discounted)}</span>`;
      } else {
        subEl.textContent = formatMoney(subtotal);
      }
    }

    const shipTh = parseInt10(cfg.shippingThreshold, 0);
    if (shipEl) {
      if (shipTh <= 0) {
        shipEl.textContent = '—';
      } else if (subtotal >= shipTh) {
        shipEl.textContent = 'Free';
      } else {
        const remain = shipTh - subtotal;
        shipEl.textContent = `${formatMoney(remain)} to free shipping`;
      }
    }

    syncShippingMilestoneSlotVisuals(stickyRoot, subtotal);
    syncTierDiscountMilestoneVisuals(stickyRoot, subtotal);

    const tier = pickActiveTieredDiscount(cfg.tieredDiscounts, subtotal);
    if (tierRow && tierEl) {
      const pct = tier ? Number(tier.valuePercent) : NaN;
      if (tier && Number.isFinite(pct) && pct > 0) {
        tierRow.hidden = false;
        tierEl.textContent = `${pct}% off`;
      } else {
        tierRow.hidden = true;
        tierEl.textContent = '—';
      }
    }

    syncMixMatchaGridTierDiscountLabels(bundleRoot || getBundleRoot(), stickyRoot, tier);

    renderSummaryPromoGifts(stickyRoot);

    updateStickyBarVisibility(stickyRoot);
    syncNwBundleSelectedLines(stickyRoot);
    syncMixGridPaidCtaStates(bundleRoot || getBundleRoot(), stickyRoot);
    updatePurchaseBlockPrices(stickyRoot, bundleRoot || getBundleRoot());
  }

  function slotHasSellingPlan(slot) {
    const sp = slot.dataset.nwSellingPlanId;
    return sp != null && String(sp).trim() !== '';
  }

  /** True if any selected paid line can subscribe (sticky carries selling plan id). */
  function hasAnySubscriptionEligiblePaidLine(stickyRoot) {
    if (!stickyRoot) return false;
    for (const slot of getFilledSlots(stickyRoot)) {
      if (slot.dataset.nwLineKind === LINE_KIND.PAID && slotHasSellingPlan(slot)) return true;
    }
    return false;
  }

  /**
   * Subscription column total: full one-time for non-subscription SKUs, +10%-off only on lines with a selling plan.
   */
  function bundleBlendedSubscriptionSubtotalCents(stickyRoot) {
    if (!stickyRoot) return 0;
    let sum = 0;
    for (const slot of getFilledSlots(stickyRoot)) {
      if (slot.dataset.nwLineKind !== LINE_KIND.PAID) continue;
      const unit = parseInt10(slot.dataset.nwUnitCents, 0);
      const qty = parseInt10(slot.dataset.nwQty, 1);
      const lineCents = unit * qty;
      if (slotHasSellingPlan(slot)) {
        sum += Math.round(lineCents * (1 - NW_PURCHASE_SUBSCRIPTION_DISCOUNT));
      } else {
        sum += lineCents;
      }
    }
    return sum;
  }

  /** Sticky v2 final price and purchase one-time option: tier-discounted paid subtotal. */
  function bundleCheckoutOneTimeDisplayCents(stickyRoot, subtotalCents) {
    const subtotal = subtotalCents != null ? subtotalCents : subtotalAmountCents(stickyRoot);
    const cfg = getStickyConfig();
    return tierDiscountedAmountCents(subtotal, cfg.tieredDiscounts || [], subtotal);
  }

  /** Purchase subscription option: blended subscription subtotal with tier discount applied. */
  function bundleCheckoutSubscriptionDisplayCents(stickyRoot) {
    const subtotal = subtotalAmountCents(stickyRoot);
    const blended = bundleBlendedSubscriptionSubtotalCents(stickyRoot);
    const cfg = getStickyConfig();
    return tierDiscountedAmountCents(blended, cfg.tieredDiscounts || [], subtotal);
  }

  /** Hide subscription CTAs when no paid sticky line has a selling plan (same rule as the subscribe purchase radio). */
  function syncSubscriptionPromoVisibility(stickyRoot, bundleRoot) {
    if (!stickyRoot) return;
    const hasSubEligible = hasAnySubscriptionEligiblePaidLine(stickyRoot);

    const stickySubscribeItem = stickyRoot.querySelector('[data-nw-sticky-subscribe-cta]');
    if (stickySubscribeItem) {
      stickySubscribeItem.classList.toggle('hidden', !hasSubEligible);
      stickySubscribeItem.toggleAttribute('hidden', !hasSubEligible);
    }

    const root = bundleRoot || getBundleRoot();
    const purchaseSubscribeCta = root?.querySelector('[data-nw-purchase-subscribe-cta]');
    if (purchaseSubscribeCta) {
      purchaseSubscribeCta.classList.toggle('hidden', !hasSubEligible);
      purchaseSubscribeCta.toggleAttribute('hidden', !hasSubEligible);
    }
  }

  /** One-time = paid subtotal; subscription = blended total. Visibility follows block setting; sub-eligibility only affects prices and default radio. */
  function updatePurchaseBlockPrices(stickyRoot, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!stickyRoot) return;

    syncSubscriptionPromoVisibility(stickyRoot, root);

    if (!root) return;
    const purchase = root.querySelector('.nw-bundle-purchase');
    if (!purchase) return;
    const purchaseSubscriptionEnabled = purchase.dataset.nwPurchaseSubscriptionEnabled !== 'false';
    const onetimeEl = purchase.querySelector('[data-nw-purchase-onetime-price]');
    const subscriptionEl = purchase.querySelector('[data-nw-purchase-subscription-price]');
    if (!purchaseSubscriptionEnabled || !onetimeEl || !subscriptionEl) return;

    const optWrap = purchase.querySelector('.nw-bundle-purchase__purchase-option');
    const subDesc = purchase.querySelector('[data-nw-purchase-subscription-description]');
    const subRow = purchase.querySelector('[data-nw-purchase-subscription-row]');

    const onetimeCents = subtotalAmountCents(stickyRoot);
    const hasSubEligible = hasAnySubscriptionEligiblePaidLine(stickyRoot);

    if (optWrap) optWrap.hidden = false;
    if (subDesc) subDesc.hidden = false;
    if (subRow) subRow.hidden = !hasSubEligible;

    if (!hasSubEligible) {
      const subRadio = purchase.querySelector('input[name="nw-bundle-purchase"][value="subscription"]');
      const oneRadio = purchase.querySelector('input[name="nw-bundle-purchase"][value="onetime"]');
      if (oneRadio) oneRadio.checked = true;
      if (subRadio) subRadio.checked = false;
    }

    if (onetimeCents <= 0) {
      onetimeEl.textContent = '—';
      subscriptionEl.textContent = '—';
      return;
    }
    onetimeEl.textContent = formatMoney(bundleCheckoutOneTimeDisplayCents(stickyRoot, onetimeCents));
    if (hasSubEligible) {
      subscriptionEl.textContent = formatMoney(bundleCheckoutSubscriptionDisplayCents(stickyRoot));
    } else {
      subscriptionEl.textContent = '—';
    }
  }

  function promoGiftQualifies(g, subtotal) {
    if (!g || g.enabled === false) return false;
    const vid = g.variantId;
    if (vid == null || vid === '' || Number(vid) === 0) return false;
    const th = Number.parseInt(String(g.threshold), 10);
    return Number.isFinite(th) && th > 0 && subtotal >= th;
  }

  /** Pill under promo-gift cells (dynamic template clones have no Liquid-rendered tags). */
  function syncPromoGiftBundleTag(slot, giftCfg) {
    const cfg = getStickyConfig();
    const t = cfg.bundleFreeGiftTag;
    if (!t || !t.enabled || !slot) return;
    if (!slot.hasAttribute('data-nw-dynamic-promo')) return;

    let wrap = slot.querySelector('.nw-bundle-sticky-bar__item-discount-tags');
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'nw-bundle-sticky-bar__item-discount-tags';
      slot.appendChild(wrap);
    }

    let span = wrap.querySelector('[data-nw-promo-gift-tag]');
    if (!span) {
      span = document.createElement('span');
      span.setAttribute('data-nw-promo-gift-tag', '');
      span.className =
        'nw-bundle-sticky-bar__item-discount-tag nw-bundle-sticky-bar__item-bundle-tag nw-bundle-sticky-bar__item-bundle-tag--free-gift';
      wrap.appendChild(span);
    }

    const base = (t.label && String(t.label).trim()) || 'Free gift';
    const n = giftCfg != null && giftCfg.slot != null ? Number(giftCfg.slot) : 0;
    span.textContent = n > 1 ? `${base} ${n}` : base;
    span.style.setProperty('--discount-tag-bg', t.bg || '#FFC107');
    span.style.setProperty('--discount-tag-color', t.fg || '#201D1E');
  }

  function refreshAllPromoGiftBundleTags(stickyRoot) {
    if (!stickyRoot) return;
    const gifts = Array.isArray(getStickyConfig().freeGifts) ? getStickyConfig().freeGifts : [];
    for (const slot of stickyRoot.querySelectorAll(
      `${SEL.stickySlot}[data-nw-line-kind="${LINE_KIND.PROMO_GIFT}"]`
    )) {
      const ps = slot.dataset.nwPromoSlot;
      const g = gifts.find((x) => String(x.slot) === String(ps));
      syncPromoGiftBundleTag(slot, g || { slot: ps });
    }
  }

  /** Drop threshold promos that no longer qualify; keep existing cells so their order stays fixed. */
  function removeUnqualifiedPromoGifts(stickyRoot, gifts, giftSubtotalCents) {
    if (!stickyRoot) return;
    for (const slot of [
      ...stickyRoot.querySelectorAll(`${SEL.stickySlot}[data-nw-line-kind="${LINE_KIND.PROMO_GIFT}"]`),
    ]) {
      const lineKey = slot.dataset.nwLineKey;
      if (!lineKey) continue;
      const g = gifts.find((x) => lineKeyForPromoGift(x.slot) === lineKey);
      if (!promoGiftQualifies(g, giftSubtotalCents)) {
        if (slot.hasAttribute('data-nw-dynamic-promo')) {
          slot.remove();
        } else {
          clearStickySlot(slot);
        }
      }
    }
  }

  function syncPromoFreeGifts(stickyRoot) {
    const cfg = getStickyConfig();
    const gifts = Array.isArray(cfg.freeGifts) ? [...cfg.freeGifts] : [];
    const giftSubtotal = giftQualificationSubtotalCents(stickyRoot);
    removeUnqualifiedPromoGifts(stickyRoot, gifts, giftSubtotal);

    gifts.sort((a, b) => {
      const ta = Number.parseInt(String(a.threshold), 10);
      const tb = Number.parseInt(String(b.threshold), 10);
      return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
    });

    for (const g of gifts) {
      if (!promoGiftQualifies(g, giftSubtotal)) continue;
      const lineKey = lineKeyForPromoGift(g.slot);
      const staticSlot = findStaticPromoGiftMilestoneSlot(stickyRoot, g.slot);
      let existing = slotByLineKey(stickyRoot, lineKey);

      if (
        existing &&
        staticSlot &&
        existing !== staticSlot &&
        existing.hasAttribute('data-nw-dynamic-promo')
      ) {
        existing.remove();
        existing = null;
      }

      if (existing) continue;

      let slot = staticSlot;
      if (!slot) {
        const insertAfter = findLastFilledItemSlot(stickyRoot);
        slot = insertDynamicPromoSlotAfter(stickyRoot, insertAfter);
      }
      if (!slot) continue;
      fillStickySlot(slot, {
        lineKey,
        kind: LINE_KIND.PROMO_GIFT,
        variantId: g.variantId,
        productId: g.productId != null ? String(g.productId) : null,
        sellingPlanId: null,
        title: g.productName || 'Gift',
        image: g.featuredImage || '',
        unitCents: 0,
        qty: 1,
        priceLabel: 'Free',
        promoSlot: String(g.slot),
      });
    }

    refreshAllPromoGiftBundleTags(stickyRoot);
  }

  /**
   * Smooth-scroll the next `.nw-bundle-item` column into view (vertical step flow). No-op if there is no next column.
   * Uses a top offset so the section clears sticky headers ({@link NW_BUNDLE_STEP_SCROLL_OFFSET_PX}).
   * @param {HTMLElement} triggerBtn — CTA inside the current column
   * @param {HTMLElement} [bundleRoot]
   */
  function scrollToNextBundleColumn(triggerBtn, bundleRoot) {
    const root = bundleRoot || getBundleRoot();
    if (!root || !triggerBtn || !root.contains(triggerBtn)) return;
    const current = triggerBtn.closest(SEL.bundleColumn);
    if (!current) return;
    const columns = [...root.querySelectorAll(SEL.bundleColumn)];
    const i = columns.indexOf(current);
    if (i < 0 || i >= columns.length - 1) return;
    const nextCol = columns[i + 1];
    const rect = nextCol.getBoundingClientRect();
    const scrollY = window.scrollY ?? window.pageYOffset ?? 0;
    const top = rect.top + scrollY - NW_BUNDLE_STEP_SCROLL_OFFSET_PX;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  function refreshStickyPriceForSlot(slot) {
    const kind = slot.dataset.nwLineKind;
    const unit = parseInt10(slot.dataset.nwUnitCents, 0);
    const qty = parseInt10(slot.dataset.nwQty, 1);
    const priceEl = slot.querySelector(SEL.stickyPrice);
    if (!priceEl) return;
    if (kind === LINE_KIND.FREE_POUCH || kind === LINE_KIND.PROMO_GIFT) {
      priceEl.textContent = 'Free';
      return;
    }
    priceEl.textContent = formatMoney(unit * qty);
  }

  function onCtaClick(btn, stickyRoot, bundleRoot) {
    const sticky = stickyRoot || getStickyRoot();
    if (!sticky) return;

    if (isFreePouchButton(btn)) {
      const key = lineKeyForFreePouch(btn);
      const existing = slotByLineKey(sticky, key);
      if (existing) {
        clearStickySlot(existing);
        btn.textContent = btn.getAttribute('data-default-text') || btn.textContent;
        btn.removeAttribute('data-nw-line-key');
        btn.classList.remove(CLS_FREE_POUCH_SELECTED);
        reconcileAfterStickyChange(sticky, bundleRoot);
        return;
      }
      const slot = findFirstEmptySlot(sticky);
      if (!slot) return;
      fillStickySlot(slot, {
        lineKey: key,
        kind: LINE_KIND.FREE_POUCH,
        variantId: btn.getAttribute('data-variant-id'),
        productId: btn.getAttribute('data-product-id'),
        sellingPlanId: btn.getAttribute('data-selling-plan-id'),
        title: btn.getAttribute('data-product-title') || '',
        image: btn.getAttribute('data-product-image') || '',
        unitCents: 0,
        qty: 1,
        priceLabel: 'Free',
      });
      btn.setAttribute('data-nw-line-key', key);
      btn.textContent = btn.getAttribute('data-selected-text') || btn.getAttribute('data-default-text') || 'Selected';
      btn.classList.add(CLS_FREE_POUCH_SELECTED);
      reconcileAfterStickyChange(sticky, bundleRoot);
      scrollToNextBundleColumn(btn, bundleRoot);
      return;
    }

    if (btn.hasAttribute(ATTR_PAID_ACTIVE)) return;

    if (!addPaidUnitSlot(sticky, btn, bundleRoot)) return;
    btn.setAttribute(ATTR_PAID_ACTIVE, 'true');
    activatePaidButtonUI(btn);
    reconcileAfterStickyChange(sticky, bundleRoot);
    if (isMainSetPaidButton(btn)) {
      scrollToNextBundleColumn(btn, bundleRoot);
    }
  }

  function onRemoveSticky(slot, bundleRoot) {
    const sticky = slot.closest(SEL.stickyBar);
    if (!sticky) return;
    const key = slot.dataset.nwLineKey;
    const kind = slot.dataset.nwLineKind;
    const root = bundleRoot || getBundleRoot();

    if (kind === LINE_KIND.PROMO_GIFT) {
      if (slot.hasAttribute('data-nw-dynamic-promo')) {
        slot.remove();
      } else {
        clearStickySlot(slot);
      }
      reconcileAfterStickyChange(sticky, bundleRoot);
      return;
    }

    if (kind === LINE_KIND.FREE_POUCH) {
      const fpBtn = root?.querySelector(`${SEL.ctaButton}[data-nw-line-key="${escapeSelectorAttr(key)}"]`) || null;
      if (fpBtn) {
        fpBtn.textContent = fpBtn.getAttribute('data-default-text') || fpBtn.textContent;
        fpBtn.removeAttribute('data-nw-line-key');
        fpBtn.classList.remove(CLS_FREE_POUCH_SELECTED);
      }
      clearStickySlot(slot);
      reconcileAfterStickyChange(sticky, bundleRoot);
      return;
    }

    if (kind === LINE_KIND.PAID) {
      const vid = slot.dataset.nwVariantId;
      const paidBtn =
        (vid &&
          root?.querySelector(
            `${SEL.ctaButton}[data-variant-id="${escapeSelectorAttr(vid)}"]:not([data-is-free-gift])`
          )) ||
        null;
      removePaidSlotElement(slot);
      if (paidBtn) {
        const remaining = vid ? paidSlotsForVariant(sticky, vid).length : 0;
        if (remaining === 0) {
          resetPaidButtonUI(paidBtn);
        } else {
          const wrap = paidBtn.closest('.nw-bundle-item__product-item-cta');
          const input = wrap?.querySelector(SEL.qtyInput);
          if (input) input.value = String(remaining);
        }
      }
      reconcileAfterStickyChange(sticky, bundleRoot);
      return;
    }

    clearStickySlot(slot);
    reconcileAfterStickyChange(sticky, bundleRoot);
  }

  function adjustQty(input, delta, stickyRoot, bundleRoot) {
    const wrap = input.closest(SEL.qtyWrap);
    const cta = wrap?.parentElement;
    const btn = cta?.querySelector(SEL.ctaButton);
    if (!btn || !btn.hasAttribute(ATTR_PAID_ACTIVE)) return;
    const sticky = stickyRoot || getStickyRoot();
    const vid = btn.getAttribute('data-variant-id');
    if (!sticky || !vid) return;
    const current = parseInt10(input.value, 1);
    if (delta < 0 && current <= 1) {
      deselectPaidLine(btn, sticky, bundleRoot);
      return;
    }

    let q = current + delta;
    const maxAttr = parseInt10(input.getAttribute('max'), 100);
    const maxAllowed = maxAttr;
    const min = parseInt10(input.getAttribute('min'), 1);
    q = Math.min(maxAllowed, Math.max(min, q));
    syncPaidSlotsToQty(sticky, bundleRoot, btn, q);
    input.value = String(paidSlotsForVariant(sticky, vid).length);
    reconcileAfterStickyChange(sticky, bundleRoot);
  }

  function bind(bundleRoot, stickyRootEl) {
    const stickyRoot = stickyRootEl || bundleRoot.querySelector(SEL.stickyBar);
    if (!stickyRoot) return;
    bundleRoot.__nwStickyRoot = stickyRoot;

    bundleRoot.addEventListener('click', (e) => {
      const purchaseCta = e.target.closest('.nw-bundle-purchase__cta-button');
      if (purchaseCta && bundleRoot.contains(purchaseCta)) {
        e.preventDefault();
        nwBundleAddToCart(nwBundlePurchaseModeFromBundleRoot(bundleRoot), bundleRoot, purchaseCta);
        return;
      }
      const btn = e.target.closest(SEL.ctaButton);
      if (btn && bundleRoot.contains(btn)) {
        e.preventDefault();
        onCtaClick(btn, stickyRoot, bundleRoot);
        return;
      }
      const dec = e.target.closest(SEL.decQty);
      if (dec && bundleRoot.contains(dec)) {
        const input = dec.closest(SEL.qtyWrap)?.querySelector(SEL.qtyInput);
        if (input) adjustQty(input, -1, stickyRoot, bundleRoot);
        return;
      }
      const inc = e.target.closest(SEL.incQty);
      if (inc && bundleRoot.contains(inc)) {
        if (inc.disabled) return;
        const input = inc.closest(SEL.qtyWrap)?.querySelector(SEL.qtyInput);
        if (input) adjustQty(input, 1, stickyRoot, bundleRoot);
        return;
      }
});

    stickyRoot.addEventListener('click', (e) => {
      const stickyBuyNow = e.target.closest('.nw-bundle-sticky-bar__cta-item-button-buy-now');
      if (stickyBuyNow) {
        e.preventDefault();
        nwBundleAddToCart(nwBundlePurchaseModeFromBundleRoot(bundleRoot), bundleRoot, stickyBuyNow);
        return;
      }
      const stickySubscribe = e.target.closest('.nw-bundle-sticky-bar__cta-item-button-subscribe');
      if (stickySubscribe) {
        e.preventDefault();
        nwBundleAddToCart('subscription', bundleRoot, stickySubscribe);
        return;
      }
      const stickyOnetime = e.target.closest('.nw-bundle-sticky-bar__cta-item-button');
      if (stickyOnetime) {
        e.preventDefault();
        nwBundleAddToCart('onetime', bundleRoot, stickyOnetime);
        return;
      }
      const rm = e.target.closest(SEL.removeSticky);
      if (!rm) return;
      const slot = rm.closest(SEL.stickySlot);
      if (slot) onRemoveSticky(slot, bundleRoot);
    });

    bundleRoot.addEventListener('change', (e) => {
      const input = e.target.closest(SEL.qtyInput);
      if (!input || !bundleRoot.contains(input)) return;
      const btn = input.closest('.nw-bundle-item__product-item-cta')?.querySelector(SEL.ctaButton);
      if (!btn || !btn.hasAttribute(ATTR_PAID_ACTIVE)) return;
      const vid = btn.getAttribute('data-variant-id');
      if (!vid) return;
      let q = parseInt10(input.value, 1);
      const max = parseInt10(input.getAttribute('max'), 100);
      const min = parseInt10(input.getAttribute('min'), 1);
      if (q <= 0) {
        deselectPaidLine(btn, stickyRoot, bundleRoot);
        return;
      }
      q = Math.min(max, Math.max(min, q));
      syncPaidSlotsToQty(stickyRoot, bundleRoot, btn, q);
      input.value = String(paidSlotsForVariant(stickyRoot, vid).length);
      reconcileAfterStickyChange(stickyRoot, bundleRoot);
    });

    reconcileAfterStickyChange(stickyRoot, bundleRoot);
  }

  function init() {
    const boundRoots = new WeakSet();
    document.querySelectorAll(SEL.stickyBar).forEach((stickyEl) => {
      const bundleRoot = resolveBundleRootForSticky(stickyEl);
      if (!bundleRoot || boundRoots.has(bundleRoot)) return;
      boundRoots.add(bundleRoot);
      bind(bundleRoot, stickyEl);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
